import React from 'react';
import BackToHome from './BackToHome';
import './LegalPages.css';

const AccountDeletion = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="site-page-topbar legal-page-topbar">
          <BackToHome />
        </div>

        <div className="legal-content">
          <h1>Account and Data Deletion</h1>
          <p className="last-updated">Last updated: January 25, 2026</p>
          
          <section>
            <h2>Overview</h2>
            <p>
              At pycrust.io, we believe in giving you complete control over your data. You can delete your account 
              at any time through your account settings. When you delete your account, we will permanently remove 
              all of your personal data and content from our systems, with the exception of operational logs used 
              exclusively for bookkeeping purposes.
            </p>
          </section>

          <section>
            <h2>How to Delete Your Account</h2>
            <p>
              To delete your account, follow these steps:
            </p>
            <ol>
              <li>Log in to your pycrust.io account</li>
              <li>Navigate to your <strong>Settings</strong> page</li>
              <li>Find the <strong>Account Deletion</strong> section</li>
              <li>Read and acknowledge all the warning checkboxes regarding data deletion</li>
              <li>Click the <strong>Delete Account</strong> button to permanently delete your account</li>
            </ol>
            <p>
              Once you click "Delete Account", your account and all associated data will be permanently deleted. 
              This action cannot be undone.
            </p>
            <p>
              If you need assistance with account deletion, please contact us at{' '}
              <a href="mailto:contact@pycrust.io">contact@pycrust.io</a>.
            </p>
          </section>

          <section>
            <h2>What Gets Deleted</h2>
            <p><strong>When you delete your account, we will permanently delete all of your user data, including:</strong></p>
            <ul>
              <li><strong>Personal Information:</strong> Your name, email address, profile picture, and account credentials</li>
              <li><strong>Platform Connections:</strong> All connected social media platform accounts and associated OAuth tokens</li>
              <li><strong>Content:</strong> All uploaded video files, thumbnails, and associated metadata stored in Google Cloud Storage</li>
              <li><strong>Content Library:</strong> Your entire video library, folder structures, and organization data</li>
              <li><strong>Scheduled Content:</strong> All scheduled uploads, draft content, and pending posts</li>
              <li><strong>Account Settings:</strong> All user preferences, settings, and configurations</li>
              <li><strong>Manager-Client Relationships:</strong> All manager-client associations and permissions</li>
              <li><strong>Beta Account Submissions:</strong> All beta account requests and associated data</li>
              <li><strong>Subscriptions:</strong> All subscription and billing information</li>
            </ul>
          </section>

          <section>
            <h2>Data Retention: Logs Only</h2>
            <p>
              The only data we retain after account deletion are operational logs used exclusively for bookkeeping purposes. 
              These logs may include:
            </p>
            <ul>
              <li>Basic operational timestamps and transaction records</li>
              <li>System audit logs for compliance and security purposes</li>
              <li>Aggregated, anonymized usage statistics (no personal identifiers)</li>
            </ul>
            <p>
              These logs are retained solely for internal record-keeping, system maintenance, and compliance purposes. 
              They do not contain personally identifiable information and cannot be used to identify individual users.
            </p>
          </section>

          <section>
            <h2>Important Notes</h2>
            <p>
              <strong>Account deletion is permanent and cannot be undone.</strong> Please ensure you have backed up any 
              content or data you wish to keep before deleting your account. This process is typically completed within 
              a short timeframe after your deletion request.
            </p>
            <p>
              Once your account is deleted, you will no longer have access to any of your content, scheduled posts, or 
              account information. If you have any scheduled posts that haven't been published yet, they will be cancelled 
              and cannot be recovered.
            </p>
          </section>

          <section>
            <h2>Questions or Concerns</h2>
            <p>
              If you have any questions about account deletion or our data retention practices, please don't hesitate to 
              contact us at{' '}
              <a href="mailto:contact@pycrust.io">contact@pycrust.io</a>.
            </p>
          </section>

          <section className="legal-page-footer-note">
            <p>&copy; 2025 pycrust.io. All rights reserved by Cube Solutions LLC.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AccountDeletion;

